import { Component, DestroyRef, ElementRef, HostListener, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { NgClickOutsideDirective } from 'ng-click-outside2';
import { SearchInputComponent } from '../../../../components/inputs/search-input/search-input.component';
import {
  SortDropdownComponent,
  SortDropdownGroup,
  buildSortGroups,
} from '../../../../components/inputs/sort-dropdown/sort-dropdown.component';
import { OrganizationsTableComponent } from '../../../../components/tables/organizations-table/organizations-table.component';
import { ContactsService } from '../../../../services/contacts/contacts.service';
import { GroupAudienceService } from '../../../../services/segments/group-audience.service';
import { IOrganization } from '../../../../types/contactModule';
import { MessageService } from '../../../../services/message/message.service';
import { PromptCardComponent } from '../../../../components/toasts-and-cards/prompt-card/prompt-card.component';
import { HasPermissionDirective } from '../../../../directives/has-permission.directive';
import { countries } from '../../../../../utils/countriesList';
import { industries } from '../../../../../utils/staticData';
import { formatCount } from '../../../../../utils/sms';
import { countNoun, humanizeEnum, sumCounts } from '../../../../components/tables/group-table/group-rows';

export type IOrganizationSort =
  | 'name,asc'
  | 'name,desc'
  | 'createdAt,asc'
  | 'createdAt,desc'
  | 'modifiedAt,asc'
  | 'modifiedAt,desc';

@Component({
  selector: 'app-organization-list',
  standalone: true,
  imports: [
    SearchInputComponent,
    SortDropdownComponent,
    OrganizationsTableComponent,
    PromptCardComponent,
    HasPermissionDirective,
    NgClickOutsideDirective,
  ],
  templateUrl: './organization-list.component.html',
  styleUrls: ['../../../../components/tables/group-table/group-list.css', './organization-list.component.css'],
})
export class OrganizationListComponent {
  contactsService = inject(ContactsService);
  messageService = inject(MessageService);
  router = inject(Router);
  private audience = inject(GroupAudienceService);
  private destroyRef = inject(DestroyRef);
  private host: ElementRef<HTMLElement> = inject(ElementRef);

  @ViewChild(OrganizationsTableComponent) tableChild?: OrganizationsTableComponent;
  @ViewChild(SearchInputComponent) searchInput?: SearchInputComponent;
  @ViewChild('filterTrigger') filterTrigger?: ElementRef<HTMLButtonElement>;
  @ViewChild('filterPanel') filterPanel?: ElementRef<HTMLElement>;

  readonly countries = countries;
  readonly industries = industries.map((industry) => ({
    value: industry.value,
    label: humanizeEnum(industry.value),
  }));

  searchTerm: string = '';
  sortCriteria: IOrganizationSort = 'name,asc';
  industryFilter = '';
  countryFilter = '';
  showFilters = false;
  total: number | null = null;

  readonly sortGroups: SortDropdownGroup[] = buildSortGroups({
    nameField: 'name',
    createdField: 'createdAt',
    modifiedField: 'modifiedAt',
  });

  selectedOrganizations: IOrganization[] = [];

  /** Search, sort and filters for the table. A new object on each change; page and size live in the table. */
  params: Record<string, any> = { sort: 'name,asc' };

  // delete selected organizations
  showDeleteOrganizationsPrompt: boolean = false;
  deleting = false;
  /** Members across the ticked organizations, for the delete prompt. */
  selectedMembers: { total: number; complete: boolean } | null = null;

  get filtersActive(): boolean {
    return !!this.industryFilter || !!this.countryFilter;
  }

  get activeFilterCount(): number {
    return (this.industryFilter ? 1 : 0) + (this.countryFilter ? 1 : 0);
  }

  get industryLabel(): string {
    return this.industries.find((industry) => industry.value === this.industryFilter)?.label ?? this.industryFilter;
  }

  get countLine(): string {
    if (this.total === null) return '';
    const line = countNoun(this.total, 'organization', 'organizations');
    return this.searchTerm.trim() || this.filtersActive ? `${line} ${this.total === 1 ? 'matches' : 'match'}` : line;
  }

  get selectionLabel(): string {
    return `${countNoun(this.selectedOrganizations.length, 'organization', 'organizations')} selected`;
  }

  get deleteTitle(): string {
    const count = this.selectedOrganizations.length;
    return count === 1 ? `Delete ${this.selectedOrganizations[0]?.name || 'this organization'}?` : `Delete ${count} organizations?`;
  }

  get deleteButton(): string {
    const count = this.selectedOrganizations.length;
    return count === 1 ? 'Delete organization' : `Delete ${count} organizations`;
  }

  get deleteImpact(): string {
    const members = this.selectedMembers;
    if (!members) return 'Counting their members…';
    const single = this.selectedOrganizations.length === 1;
    if (members.total === 0 && members.complete) return single ? 'It has no contacts.' : 'They have no contacts.';
    if (!members.complete) return 'Their contacts leave these organizations and stay in your contact list.';
    if (single) {
      return `${countNoun(members.total, 'contact leaves', 'contacts leave')} this organization. They stay in your contact list.`;
    }
    // A contact can belong to several of these organizations, so the sum is an upper bound.
    return `Up to ${countNoun(members.total, 'contact leaves', 'contacts leave')} these organizations. They stay in your contact list.`;
  }

  handleAction(action: string) {
    if (this.selectedOrganizations.length === 0) return;
    switch (action) {
      case 'send-sms':
        this.sendSMStoSelectedContacts();
        break;
      case 'delete':
        this.openDeleteOrganizationsPrompt();
        break;
    }
  }

  closeDeleteOrganizationsPrompt() {
    if (this.deleting) return;
    this.showDeleteOrganizationsPrompt = false;
  }

  openDeleteOrganizationsPrompt() {
    this.showDeleteOrganizationsPrompt = true;
    this.selectedMembers = null;
    const counts = this.selectedOrganizations
      .filter((org) => !!org.uuid)
      .map((org) =>
        this.audience.memberCount({ kind: 'organization', uuid: org.uuid! }).pipe(
          take(1),
          catchError(() => of(null))
        )
      );
    if (!counts.length) {
      this.selectedMembers = { total: 0, complete: false };
      return;
    }
    forkJoin(counts)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((values) => (this.selectedMembers = sumCounts(values)));
  }

  deleteSelectedOrganizations() {
    const count = this.selectedOrganizations.length;
    if (!count || this.deleting) return;
    this.deleting = true;
    this.contactsService
      .deleteSelectedOrganizations(this.selectedOrganizations.map((org) => org.uuid as string))
      .subscribe({
        next: () => {
          this.deleting = false;
          this.selectedOrganizations.forEach((org) =>
            this.audience.invalidate({ kind: 'organization', uuid: org.uuid as string })
          );
          this.resetSelectedOrganizations();
          this.showDeleteOrganizationsPrompt = false;
        },
        error: () => {
          this.deleting = false;
          this.showDeleteOrganizationsPrompt = false;
          this.contactsService.showError(
            count === 1 ? 'The organization was not deleted. Try again.' : `The ${count} organizations were not deleted. Try again.`
          );
        },
      });
  }

  // ── Search, sort, filters ──
  updateSearchTerm(term: string) {
    this.searchTerm = term ?? '';
    this.refresh();
  }

  updateSortCriteria(criteria: IOrganizationSort) {
    this.sortCriteria = criteria;
    this.refresh();
  }

  toggleFilters() {
    if (this.showFilters) {
      this.closeFilters();
      return;
    }
    this.showFilters = true;
    // Into the panel once it renders.
    setTimeout(() => this.filterPanel?.nativeElement.querySelector<HTMLElement>('select')?.focus());
  }

  /** Done, Escape or a click outside. Focus inside the panel goes back to the Filters button. */
  closeFilters() {
    if (!this.showFilters) return;
    const active = this.host.nativeElement.ownerDocument?.activeElement;
    const focusInside = !!active && !!this.filterPanel?.nativeElement.contains(active);
    this.showFilters = false;
    if (focusInside) this.filterTrigger?.nativeElement.focus();
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    if (this.showFilters) this.closeFilters();
  }

  setIndustryFilter(value: string) {
    this.industryFilter = value || '';
    this.refresh();
  }

  setCountryFilter(value: string) {
    this.countryFilter = value || '';
    this.refresh();
  }

  clearFilter(which: 'industry' | 'country') {
    if (which === 'industry') this.industryFilter = '';
    if (which === 'country') this.countryFilter = '';
    this.refresh();
  }

  clearAllFilters() {
    this.industryFilter = '';
    this.countryFilter = '';
    this.refresh();
  }

  clearSearchAndFilters() {
    this.searchInput?.writeValue('');
    this.searchTerm = '';
    this.clearAllFilters();
  }

  /** Rebuilds the query; the table loads page one when it changes. */
  private refresh() {
    const params: Record<string, any> = { sort: this.sortCriteria };
    const term = this.searchTerm.trim();
    if (term) params['name.contains'] = term;
    if (this.industryFilter) params['industry.equals'] = this.industryFilter;
    if (this.countryFilter) params['country.equals'] = this.countryFilter;
    this.params = params;
  }

  get reactiveParams() {
    return this.params;
  }

  updateTotal(total: number) {
    this.total = total;
  }

  updateSelectedOrganizations(organizations: IOrganization[]) {
    this.selectedOrganizations = [...organizations];
  }

  resetSelectedOrganizations() {
    this.selectedOrganizations = [];
    this.tableChild?.deSelectAllOrganizations();
    this.keepFocusOnPage();
  }

  /**
   * The selection bar goes when nothing is ticked. If it held focus, move
   * focus to the search box rather than letting it drop to the page top.
   */
  private keepFocusOnPage() {
    setTimeout(() => {
      const active = this.host.nativeElement.ownerDocument?.activeElement;
      if (active && active !== active.ownerDocument.body) return;
      this.host.nativeElement.querySelector<HTMLInputElement>('.gl-search input')?.focus();
    });
  }

  sendSMStoSelectedContacts() {
    this.messageService.updateSelectedOrganizations(this.selectedOrganizations);
    this.router.navigate(['/organization/messages/create']);
    this.resetSelectedOrganizations();
  }

  readonly formatCount = formatCount;
}
